/** The texts of users' profiles, spread into the app's dictionaries (`lib/i18n.tsx`). */
export const peopleEn = {
  publicProfile: 'How others see you',
  publicCardHint: 'Signed-in users see this card. Select a line to change it.',
  neverShown: 'Never shown to others: your e-mail address, username, specialty, experience and results.',
  profileNameHint: 'Your name comes from your account. A change there shows after your next login.',
  photo: 'Your photo',
  photoChange: 'Change your photo',
  photoConfirm: 'This photo shows me, and no patient or medical image.',
  photoCrop: 'Photo: drag it or use the arrow keys to move it, plus and minus to zoom',
  photoZoom: 'Zoom',
  photoWhere: 'Where it shows',
  photoOnProfile: 'Profile',
  photoInLists: 'Lists',
  photoInPosts: 'Posts',
  photoMove: 'Drag to move. Zoom with the slider, the mouse wheel or two fingers.',
  photoAnyPicture: 'Any picture your browser opens, at any size: only the square is sent.',
  photoVisible: 'Every signed-in user sees it beside your name. Platform admins remove a photo that breaks the rule.',
  photoKept: 'Sent as a square of 512 pixels and kept as 256 pixels, without location, camera or other file data.',
  photoUse: 'Confirm and use',
  photoChooseAnother: 'Choose another',
  photoUnreadable: 'This browser cannot open the picture. Choose a JPEG, PNG or WebP picture.',
  photoRemove: 'Remove the photo',
  photoRemoveWarning: 'Remove this user’s photo for breaking the rule? It is deleted, and the audit log records it.',
  rankedChoice: 'Name me in my sites’ reader ranking',
  rankedChoiceHint: 'Otherwise readers see your place without your name. Your agreement with others is never shown.',
  honorificPrefix: 'Titles before your name',
  honorificPrefixPlaceholder: 'Titles',
  honorificSuffix: 'Titles after your name',
  honorificSuffixPlaceholder: 'titles after',
  position: 'Position',
  positionPlaceholder: 'Add your position, such as consultant paediatric radiologist',
  affiliation: 'Institution and department',
  affiliationPlaceholder: 'Add your institution and department',
  orcid: 'ORCID iD',
  about: 'About you',
  aboutPlaceholder: 'Add a few words about your work and interests. No patient data.',
  aboutCount: '{count} of {max} characters',
  profileEdit: 'Edit your profile',
  profileInactive: 'This account is no longer active.',
  readingExperience: 'Specialty and experience',
  account: 'Account',
  accountDeactivate: 'Deactivate account',
  accountDeactivateWarning: 'They can no longer log in, and their sessions and API tokens stop at once. What they contributed stays theirs.',
  accountReactivate: 'Reactivate account',
  accountErase: 'Erase account',
  accounts: 'Accounts',
  accountsIntro: 'Every account of the platform, with its sites and roles. Open an account to deactivate, reactivate or erase it. Site admins add people to their sites and change their roles there.',
  accountsSearch: 'Name, username or e-mail address',
  accountState: 'State',
  accountsEveryState: 'Every state',
  accountState_active: 'Active',
  accountState_deactivated: 'Deactivated',
  accountState_erased: 'Erased',
  accountsWithoutSite: 'Without a site',
  accountsNoSite: 'No site',
  accountsCount: '{count} accounts',
  accountsCount_one: '1 account',
  accountsRoles: 'Roles',
  accountsLastSignIn: 'Last sign-in',
  accountsNeverSignedIn: 'Never',
  accountsMore: 'Load more accounts',
  accountEraseWarning: 'Only at the former user’s request, once you confirmed who asks. Their login, e-mail address, profile and photo, preferences, tokens, memberships, personal collections, polls and course data are deleted, and their name comes off their forum posts. Their name stays on their annotations, readings, reports and audit entries. This cannot be undone.',
  accountEraseForumPosts: 'Delete their forum posts too, not only their name on them',
  myData: 'Your data',
  myDataIntro: 'Download what the platform keeps about you as a JSON file: account, profile, roles, preferences, tokens, notifications, uploads, forum posts, polls, your own actions, and how much you contributed per site. To have your account erased, write to the operator, as the privacy notice says.',
  myDataDownload: 'Download my data',
}

export const peopleDe: Record<keyof typeof peopleEn, string> = {
  publicProfile: 'So sehen andere Sie',
  publicCardHint: 'Angemeldete Personen sehen diese Karte. Wählen Sie eine Zeile, um sie zu ändern.',
  neverShown: 'Anderen nie gezeigt: Ihre E-Mail-Adresse, Ihr Benutzername, Fachgebiet, Ihre Erfahrung und Ergebnisse.',
  profileNameHint: 'Ihr Name stammt aus Ihrem Konto. Eine Änderung dort erscheint nach der nächsten Anmeldung.',
  photo: 'Ihr Foto',
  photoChange: 'Foto ändern',
  photoConfirm: 'Dieses Foto zeigt mich und keine Patientin, keinen Patienten und kein medizinisches Bild.',
  photoCrop: 'Foto: ziehen oder mit den Pfeiltasten verschieben, mit Plus und Minus zoomen',
  photoZoom: 'Zoom',
  photoWhere: 'Wo es erscheint',
  photoOnProfile: 'Profil',
  photoInLists: 'Listen',
  photoInPosts: 'Beiträge',
  photoMove: 'Zum Verschieben ziehen. Zoomen mit dem Regler, dem Mausrad oder zwei Fingern.',
  photoAnyPicture: 'Jedes Bild, das Ihr Browser öffnet, in jeder Größe: Gesendet wird nur das Quadrat.',
  photoVisible: 'Alle angemeldeten Personen sehen es neben Ihrem Namen. Plattform-Admins entfernen ein Foto, das gegen die Regel verstößt.',
  photoKept: 'Gesendet als Quadrat mit 512 Pixeln und gespeichert mit 256 Pixeln, ohne Ort, Kamera oder andere Dateiangaben.',
  photoUse: 'Bestätigen und verwenden',
  photoChooseAnother: 'Anderes wählen',
  photoUnreadable: 'Dieser Browser kann das Bild nicht öffnen. Wählen Sie ein JPEG-, PNG- oder WebP-Bild.',
  photoRemove: 'Foto entfernen',
  photoRemoveWarning: 'Das Foto dieser Person wegen eines Regelverstoßes entfernen? Es wird gelöscht, und das Audit-Log hält es fest.',
  rankedChoice: 'Mich in der Rangfolge der Befundenden meiner Standorte nennen',
  rankedChoiceHint: 'Sonst sehen Befundende Ihren Platz ohne Ihren Namen. Ihre Übereinstimmung mit anderen wird nie gezeigt.',
  honorificPrefix: 'Titel vor dem Namen',
  honorificPrefixPlaceholder: 'Titel',
  honorificSuffix: 'Titel nach dem Namen',
  honorificSuffixPlaceholder: 'Titel danach',
  position: 'Position',
  positionPlaceholder: 'Position ergänzen, etwa Oberärztin für Kinderradiologie',
  affiliation: 'Institution und Abteilung',
  affiliationPlaceholder: 'Institution und Abteilung ergänzen',
  orcid: 'ORCID iD',
  about: 'Über Sie',
  aboutPlaceholder: 'Ein paar Worte zu Ihrer Arbeit und Ihren Interessen. Keine Patientendaten.',
  aboutCount: '{count} von {max} Zeichen',
  profileEdit: 'Profil bearbeiten',
  profileInactive: 'Dieses Konto ist nicht mehr aktiv.',
  readingExperience: 'Fachgebiet und Erfahrung',
  account: 'Konto',
  accountDeactivate: 'Konto deaktivieren',
  accountDeactivateWarning: 'Die Person kann sich nicht mehr anmelden; ihre Sitzungen und API-Tokens enden sofort. Was sie beigetragen hat, bleibt ihres.',
  accountReactivate: 'Konto reaktivieren',
  accountErase: 'Konto löschen',
  accounts: 'Konten',
  accountsIntro: 'Alle Konten der Plattform mit ihren Standorten und Rollen. Öffnen Sie ein Konto, um es zu deaktivieren, zu reaktivieren oder zu löschen. Standort-Admins nehmen Personen in ihre Standorte auf und ändern dort ihre Rollen.',
  accountsSearch: 'Name, Benutzername oder E-Mail-Adresse',
  accountState: 'Zustand',
  accountsEveryState: 'Jeder Zustand',
  accountState_active: 'Aktiv',
  accountState_deactivated: 'Deaktiviert',
  accountState_erased: 'Gelöscht',
  accountsWithoutSite: 'Ohne Standort',
  accountsNoSite: 'Kein Standort',
  accountsCount: '{count} Konten',
  accountsCount_one: '1 Konto',
  accountsRoles: 'Rollen',
  accountsLastSignIn: 'Letzte Anmeldung',
  accountsNeverSignedIn: 'Nie',
  accountsMore: 'Weitere Konten laden',
  accountEraseWarning: 'Nur auf Wunsch der früheren Nutzerin oder des früheren Nutzers, nachdem Sie geprüft haben, wer fragt. Anmeldung, E-Mail-Adresse, Profil und Foto, Einstellungen, Tokens, Mitgliedschaften, persönliche Sammlungen, Umfragen und Kursdaten werden gelöscht, und der Name verschwindet von den Forumsbeiträgen. An Annotationen, Befundungen, Berichten und Einträgen im Audit-Log bleibt der Name. Das lässt sich nicht rückgängig machen.',
  accountEraseForumPosts: 'Auch die Forumsbeiträge löschen, nicht nur den Namen daran',
  myData: 'Ihre Daten',
  myDataIntro: 'Laden Sie herunter, was die Plattform über Sie speichert, als JSON-Datei: Konto, Profil, Rollen, Einstellungen, Tokens, Benachrichtigungen, Uploads, Forumsbeiträge, Umfragen, Ihre eigenen Aktionen und wie viel Sie je Standort beigetragen haben. Für die Löschung Ihres Kontos schreiben Sie an den Betreiber, wie der Datenschutzhinweis sagt.',
  myDataDownload: 'Meine Daten herunterladen',
}
